import { useEffect, useRef, type ReactNode } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import {
  activeThemeImageUrl,
  applyTheme,
  currentThemeId,
  getTheme,
  setTheme,
} from '@/features/theme/themes';

const STORAGE = 'winr-skin';
const PRELOAD_ID = 'winr-theme-preload';

/**
 * Owns theme application at runtime. The saved theme is applied *before paint*
 * synchronously in `main.tsx` (no flash); this provider then:
 *  - preloads the active theme's image (`<link rel="preload" as="image">`) so the
 *    full-res background paints without a flash on first navigation;
 *  - mirrors the signed-in user's `profiles.skin_id` down on first load, so a
 *    choice made on another device follows them — but only when this device has
 *    no explicit localStorage selection yet (never clobbers a local choice).
 *
 * The accent color is fixed (electric blue, globals.css) and not user-changeable.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const syncedFor = useRef<string | null>(null);

  // Keep a single <link rel="preload"> in <head> pointed at the active image.
  useEffect(() => {
    const url = activeThemeImageUrl(currentThemeId());
    let link = document.getElementById(PRELOAD_ID) as HTMLLinkElement | null;
    if (!url) {
      link?.remove();
      return;
    }
    if (!link) {
      link = document.createElement('link');
      link.id = PRELOAD_ID;
      link.rel = 'preload';
      link.as = 'image';
      document.head.appendChild(link);
    }
    link.href = url;
  });

  // Cross-device: adopt the server-side preference once, if no local choice.
  useEffect(() => {
    const serverId = profile?.skin_id;
    if (!serverId || syncedFor.current === profile?.id) return;
    syncedFor.current = profile?.id ?? null;
    const hasLocalChoice = localStorage.getItem(STORAGE);
    if (!hasLocalChoice && getTheme(serverId) && serverId !== currentThemeId()) {
      setTheme(serverId);
    }
  }, [profile?.id, profile?.skin_id]);

  // Re-assert on mount (covers HMR / late provider mounting).
  useEffect(() => {
    applyTheme(currentThemeId());
  }, []);

  return <>{children}</>;
}
